
import { select, Selection } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { max } from "d3";
import { axisBottom, axisLeft } from "d3-axis";
import { Vue, Options } from "vue-class-component";
import { Ref, Prop } from "vue-property-decorator";
import { UserPDStats } from "../../types";
import ActionButton from "./ActionButton.vue";
@Options({
  components: { ActionButton }
})
export default class PDAnalyticsChart extends Vue {
  @Prop({ type: Array, required: true }) stats!: UserPDStats;
  @Prop({ type: Number }) size = 0;
  @Prop({ type: Boolean }) expand = false;
  @Prop({ type: Boolean }) zoom: "in" | "out" = "out";
  @Ref("container") readonly container!: HTMLElement;
  draw() {
    if (!this.stats || !this.stats.axis) return;
    select(this.container)
      .select("svg")
      .remove();
    const dataset = this.stats.axis.map(a => [a.dynamicValue, a.staticValue]);
    const width = this.size;
    const height = this.size;
    const marginTop = 8 + (this.expand ? 0 : 0);
    const marginBottom = 32 + (this.expand ? 64 : 0);
    const marginRight = 8 + (this.expand ? 0 : 0);
    const marginLeft = 8 + (this.expand ? 64 : 0);
    const margin = {
      top: marginTop,
      bottom: marginBottom,
      right: marginRight,
      left: marginLeft
    };
    const svg = select(this.container)
      .append("svg")
      .attr("width", width)
      .attr("height", height);
    const isOut = this.zoom === "out";
    const xMax = isOut ? 1 : 0;
    const yMax = isOut ? 1 : 0;
    const xMin = isOut
      ? 0
      : Math.min.apply(
          null,
          dataset.map(d => d[0])
        );
    const yMin = isOut
      ? 0
      : Math.min.apply(
          null,
          dataset.map(d => d[1])
        );
    const lrBound = isOut
      ? 0.8
      : Math.max.apply(
          null,
          dataset.map(d => d[0])
        ) - 0.2;
    const ticks = isOut ? 10 : 5;
    const xScale = scaleLinear()
      // @ts-ignore
      .domain([
        xMin,
        max(dataset, d => {
          return Math.max(xMax, d[0]);
        })
      ])
      .range([margin.left, width - margin.right]);

    const yScale = scaleLinear()
      // @ts-ignore
      .domain([
        yMin,
        max(dataset, d => {
          return Math.max(yMax, d[1]);
        })
      ])
      .range([height - margin.bottom + marginTop, margin.top]);

    const axisx = axisBottom(xScale).ticks(ticks);
    const axisy = axisLeft(yScale).ticks(ticks);
    if (!this.expand) {
      // @ts-ignore
      axisx.tickFormat("");
      // @ts-ignore
      axisy.tickFormat("");
    }
    if (this.expand) {
      axisx.tickSize(-height + marginBottom);
      axisy.tickSize(-width + marginRight + marginLeft);
    }

    svg
      .append("g")
      .attr("transform", "translate(" + 0 + "," + (height - margin.bottom + marginTop) + ")")
      .style("color", "#E0E0E0")
      .call(axisx)
      .selectAll(".tick text")
      .attr("fill", "gray");
    const appendText = (
      el: Selection<SVGGElement, unknown, null, undefined>,
      text: string,
      offsetX?: number,
      offsetY?: number
    ) => {
      const result = el
        .append("text")
        .attr("fill", "grey")
        .attr("text-anchor", "middle")
        .attr("font-size", "10pt")
        .text(text);
      if (offsetX) {
        result.attr("x", offsetX);
      }
      if (offsetY) {
        result.attr("y", offsetY);
      }
      return result;
    };
    if (this.expand) {
      const x = svg.append("g").attr("transform", "translate(" + 0 + "," + (height - margin.bottom + marginTop) + ")");
      appendText(x, "社会性・欲求", (width - margin.left - margin.right) / 2 + margin.left, 35);
      appendText(x, "(変わりやすいもの)", (width - margin.left - margin.right) / 2 + margin.left, 55);
    }

    svg
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + 0 + ")")
      .style("color", "#E0E0E0")
      .call(axisy)
      .selectAll(".tick text")
      .attr("fill", "gray");
    if (this.expand) {
      const y = svg.append("g").attr("transform", "translate(" + margin.left + "," + marginTop + ")");
      appendText(y, "性格・パーソナリティ", 15 - margin.left, (height - margin.top) / 2 - margin.top).attr("writing-mode", "tb");
      appendText(y, "(変わりにくいもの)", 35 - margin.left, (height - margin.top) / 2 - margin.top).attr("writing-mode", "tb");
    }

    const circleG = svg
      .append("g")
      .selectAll("circle")
      .data(dataset)
      .enter();
    circleG
      .append("circle")
      .attr("cx", d => {
        return xScale(d[0]);
      })
      .attr("cy", d => {
        return yScale(d[1]);
      })
      .attr("fill", "black")
      .attr("r", 4);
    if (this.expand) {
      circleG
        .append("text")
        .attr("fill", (_val, index: number) => {
          return this.stats.axis[index].partner.id.match(/^APL#.+?#$/)
            ? "#ff6d00"
            : this.stats.axis[index].partner.id.match(/^.+#retired#$/)
            ? "#4a4a4a"
            : "#2962ff";
        })
        .style("font-weight", "600")
        .attr("class", (_d, index) => `display-name-${index}`)
        .text((_val, index: number) => {
          return `${this.stats.axis[index].partner.lastName} ${this.stats.axis[index].partner.firstName}`;
        })
        .attr("x", function(d, index, el) {
          setTimeout(() => {
            if (d[0] >= lrBound) {
              const bbox = this.getBBox();
              const self = select(el[index]);
              // @ts-ignore
              select(el[index]).attr("x", parseFloat(self.attr("x")) - bbox.width - 22);
            }
          });
          return xScale(d[0]) + 12;
        })
        .attr("y", d => yScale(d[1]) + 5);
    }
  }
  updated() {
    this.draw();
  }
  mounted() {
    this.draw();
  }
}
